import * as Sentry from '@sentry/react';
import { normalizeEndpoint } from './utils';

export type AuthErrorName =
  | 'auth.token_missing_at_write'
  | 'auth.token_write_readback_mismatch'
  | 'auth.oauth_response_missing_token'
  | 'auth.facebook_missing_email'
  | 'auth.redirect_missing_token'
  | 'auth.unexpected_401'
  | 'auth.login_failed_post_fetch'
  | 'auth.login_on_auth_subdomain'
  | 'auth.untrusted_return_url'
  | 'auth.session_storage_unavailable'
  | 'auth.confirmation_no_token'
  | 'auth.complete_profile_no_token';

export interface AuthErrorContext {
  flowId?: string | null;
  endpoint?: string;
  status?: number;
  serverMessage?: string;
  debugId?: string;
  jwtSegments?: number;
  jwtTotalLen?: number;
  jwtSigLen?: number;
  jwtPayloadLen?: number;
  storageError?: string;
  [key: string]: unknown;
}

/**
 * Capture auth-specific errors with consistent grouping and tags.
 *
 * Fingerprint is [auth-error, errorName, endpoint] so all captures of the same
 * auth failure mode on the same endpoint group into one issue. Tags are
 * queryable in Sentry: `auth.error_type`, `auth.debug_id`, `auth.endpoint`.
 *
 * `options.level` defaults to 'error'. Use 'warning' for auth failures that
 * are expected on some user journeys (e.g. unexpected 401 after session
 * expiry).
 *
 * `options.fingerprintSuffix` lets callers fragment grouping by a sub-category
 * (e.g. QuotaExceededError vs SecurityError) without losing the top-level
 * auth-error shape.
 */
export function captureAuthError(
  errorName: AuthErrorName,
  context: AuthErrorContext = {},
  options: {
    level?: Sentry.SeverityLevel;
    fingerprintSuffix?: string;
  } = {},
): void {
  const endpoint = normalizeEndpoint(context.endpoint);
  const level = options.level ?? 'error';
  const fingerprint = options.fingerprintSuffix
    ? ['auth-error', errorName, endpoint, options.fingerprintSuffix]
    : ['auth-error', errorName, endpoint];

  Sentry.withScope((scope) => {
    scope.setTag('auth.error_type', errorName);
    scope.setTag('auth.endpoint', endpoint);
    if (context.flowId) scope.setTag('auth.flow_id', context.flowId);
    if (context.debugId) scope.setTag('auth.debug_id', context.debugId);
    if (context.status !== undefined) scope.setTag('api.status', String(context.status));
    if (context.jwtSegments !== undefined) scope.setTag('auth.jwt_segments', String(context.jwtSegments));
    if (typeof context.storageError === 'string') scope.setTag('auth.storage_error', context.storageError);

    scope.setContext('auth', context);
    scope.setLevel(level);
    scope.setFingerprint(fingerprint);
    Sentry.captureException(new Error(errorName));
  });
}
